import { formatDate, parseDate } from '@travelata-libs/utils';
import { HOTEL_CATEGORIES } from '@/entities/hotelCategory/@/analytics';
import { MEALS } from '@/entities/meal/@/analytics';
import type { SearchCriteria } from '@/entities/searchCriteria/@/analytics';
import { isInfant } from '@/shared/lib/touristAge';
import { MINDBOX_DATE_FORMAT, MINDBOX_INFANT_AGE } from '../config';
import type { MindboxCustomFields, MindboxSerpClickSource, MindboxSerpType } from '../model/mindbox';

type MindboxRawFieldValue = string | number | readonly (string | number | null | undefined)[] | null | undefined;

type MindboxRawFields = Record<string, MindboxRawFieldValue>;

type MindboxFlag = 0 | 1;

type MindboxHotelStars = (string | number)[];

const HOTEL_SERP_TYPE: MindboxSerpType = 'hotel';

const STARS_CATEGORY_NAME_MAX_LENGTH = 2;

const TWO_STARS = 2;

const ONE_STAR = 1;

const toFlag = (value: boolean): MindboxFlag => (value ? 1 : 0);

function toMindboxDate(value: string): string {
  const date = parseDate(value);

  return date ? formatDate(date, MINDBOX_DATE_FORMAT) : value;
}

function isEmptyMindboxField(value: MindboxRawFieldValue): boolean {
  if (Array.isArray(value)) return value.length === 0 || value[0] === null || value[0] === undefined;

  return value === null || value === undefined;
}

function getCityFrom(serpType: MindboxSerpType, { fromCity }: SearchCriteria): string | undefined {
  return serpType === HOTEL_SERP_TYPE ? undefined : fromCity?.name;
}

function getDestinationFields({ destination }: SearchCriteria): MindboxRawFields {
  const country = destination && destination.kind !== 'none' ? destination.country : null;
  const resorts = destination?.kind === 'resorts' ? destination.resorts : [];

  return {
    countryId: country ? [country.id] : [],
    countryName: country ? [country.name] : [],
    resortId: resorts.map(({ id }) => id),
    resortName: resorts.map(({ name }) => name),
  };
}

export function removeEmptyMindboxFields(fields: MindboxRawFields): MindboxCustomFields {
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => !isEmptyMindboxField(value)),
  ) as MindboxCustomFields;
}

export function getMindboxDatesFields({ dateRange, nights }: SearchCriteria): MindboxRawFields {
  return {
    dateFrom: dateRange?.from ? toMindboxDate(dateRange.from) : 0,
    dateTo: dateRange?.to ? toMindboxDate(dateRange.to) : 0,
    nightsfrom: nights?.from || 0,
    nightsto: nights?.to || 0,
  };
}

export function getMindboxTouristsFields({ tourists }: SearchCriteria): MindboxRawFields {
  const [room] = tourists?.rooms ?? [];
  const ages = room?.childrenAges ?? [];

  return {
    adults: room?.adults ?? 0,
    children: ages.length,
    ...Object.fromEntries(
      ages.map((age, index) => [`childrenage${index + 1}`, isInfant(age) ? MINDBOX_INFANT_AGE : age]),
    ),
  };
}

export function getMindboxHotelStars(
  hotelCategoryIds: readonly number[],
  serpType: MindboxSerpType,
): MindboxHotelStars {
  const stars = hotelCategoryIds.flatMap((hotelCategoryId): MindboxHotelStars => {
    const category = HOTEL_CATEGORIES.find(({ id }) => id === hotelCategoryId);

    if (!category) return [];

    return [category.name.length > STARS_CATEGORY_NAME_MAX_LENGTH ? category.name : category.stars];
  });
  const shouldAddOneStar = serpType !== HOTEL_SERP_TYPE && stars.includes(TWO_STARS) && !stars.includes(ONE_STAR);

  return shouldAddOneStar ? [...stars, ONE_STAR] : stars;
}

export function getMindboxMealNames(mealIds: readonly number[]): string[] {
  return mealIds.flatMap((mealId) => MEALS.find(({ id }) => id === mealId)?.name ?? []);
}

export function getMindboxSerpFields(serpType: MindboxSerpType, criteria: SearchCriteria): MindboxCustomFields {
  return removeEmptyMindboxFields({
    ...getMindboxDatesFields(criteria),
    ...getMindboxTouristsFields(criteria),
    serptype: serpType,
    cityFrom: getCityFrom(serpType, criteria),
    ...getDestinationFields(criteria),
    filterhighconfirmation: 0,
    filterhotelstars: getMindboxHotelStars(criteria.hotelCategories ?? [], serpType),
    filtermeal: getMindboxMealNames(criteria.meals ?? []),
  });
}

export function getMindboxSerpClickFields(
  serpType: MindboxSerpType,
  { criteria, searchCriteria, filters }: MindboxSerpClickSource,
): MindboxCustomFields {
  return removeEmptyMindboxFields({
    ...getMindboxTouristsFields(criteria),
    ...getMindboxDatesFields(criteria),
    serptype: serpType,
    filterbeachtype: filters.beachType,
    filteramenities: filters.amenities,
    filterbudgetfrom: filters.budgetFrom,
    filterbudgetto: filters.budgetTo,
    filterclosetoski: toFlag(filters.closeToSki),
    filterexclusive: toFlag(filters.exclusive),
    filterhotelrating: filters.hotelRating,
    filterlinebeach: filters.beachLines,
    filterownedbeach: toFlag(filters.ownedBeach),
    filterwateractivities: filters.waterActivities,
    filterchildservice: filters.childServices,
    cityFrom: getCityFrom(serpType, criteria),
    ...getDestinationFields(searchCriteria),
    filterhighconfirmation: 0,
    filterhotelstars: getMindboxHotelStars(criteria.hotelCategories ?? [], serpType),
    filtermeal: getMindboxMealNames(criteria.meals ?? []),
    filtertouroperators: filters.tourOperators,
  });
}
